import { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import StoreIcon from "@mui/icons-material/Store";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

const PAGE_SIZE = 5;

/**
 * Props
 *   deliveryCenter    {object}
 *   onStoreDragStart  {(e, store) => void}  Drag store out of this DC
 *   onStoreDragEnd    {() => void}
 *   onMoveToUnmapped  {(store) => void}     Kebab → move back to unmapped
 */
export default function StoresTable({
  deliveryCenter,
  onStoreDragStart,
  onStoreDragEnd,
  onMoveToUnmapped,
}) {
  const [page, setPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const paginatedStores = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return deliveryCenter.stores.slice(start, start + PAGE_SIZE);
  }, [page, deliveryCenter]);

  const totalPages = Math.ceil(deliveryCenter.stores.length / PAGE_SIZE);

  const handleSelectAll = () => {
    setSelectedRows(
      selectedRows.length === deliveryCenter.stores.length
        ? []
        : deliveryCenter.stores.map((_, i) => i),
    );
  };

  const handleSelectRow = (index) => {
    setSelectedRows((prev) =>
      prev.includes(index) ? prev.filter((x) => x !== index) : [...prev, index],
    );
  };

  return (
    <Box>
      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell width={44}>
                <Checkbox
                  size="small"
                  checked={
                    selectedRows.length === deliveryCenter.stores.length &&
                    deliveryCenter.stores.length > 0
                  }
                  indeterminate={
                    selectedRows.length > 0 &&
                    selectedRows.length < deliveryCenter.stores.length
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Store</TableCell>
              <TableCell>Address</TableCell>
              <TableCell align="center">Batch</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedStores.map((store, index) => (
              <TableRow
                key={index}
                hover
                draggable
                onDragStart={(e) => onStoreDragStart?.(e, store)}
                onDragEnd={onStoreDragEnd}
                sx={{
                  cursor: "grab",
                  userSelect: "none",
                  "&:active": { cursor: "grabbing" },
                }}
              >
                <TableCell>
                  <Checkbox
                    size="small"
                    checked={selectedRows.includes(index)}
                    onChange={() => handleSelectRow(index)}
                  />
                </TableCell>

                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {/* Drag handle */}
                    <Box
                      sx={{
                        color: "text.disabled",
                        fontSize: 16,
                        cursor: "grab",
                        flexShrink: 0,
                      }}
                      title="Drag to reassign or move to Unmapped"
                    >
                      ⠿
                    </Box>
                    <Avatar sx={{ bgcolor: "#E3F2FD", width: 32, height: 32 }}>
                      <StoreIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {store.details?.storeName}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                      >
                        {store.details?.storeOwnerName}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {store.details?.address}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {store.details?.city}, {store.details?.state}
                  </Typography>
                </TableCell>

                <TableCell align="center">
                  <Chip
                    color="primary"
                    variant="outlined"
                    size="small"
                    label={deliveryCenter.batchTitle}
                  />
                </TableCell>

                <TableCell align="center">
                  <Tooltip title="More">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setAnchorEl(e.currentTarget);
                        setSelectedStore(store);
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={2}
        py={1.5}
      >
        <Typography variant="caption" color="text.secondary">
          {selectedRows.length > 0
            ? `${selectedRows.length} selected`
            : "Drag a store to reassign it"}
        </Typography>
        <Pagination
          size="small"
          page={page}
          count={totalPages}
          onChange={(_, value) => setPage(value)}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem>
          <LocalShippingIcon sx={{ mr: 1 }} />
          Assign Vendor
        </MenuItem>
        <MenuItem
          onClick={() => {
            onMoveToUnmapped?.(selectedStore);
            setAnchorEl(null);
          }}
        >
          Move to Unmapped
        </MenuItem>
        <MenuItem>View on Map</MenuItem>
        <MenuItem>Change Delivery Center</MenuItem>
        <MenuItem sx={{ color: "error.main" }}>Remove</MenuItem>
      </Menu>
    </Box>
  );
}
